import { useEffect, useState } from "react";
import Preference from "../UI/Preference";
import Pagination from "../UI/Pagination";
import {
  adjustPageIndex,
  getHeaderCounterText,
  getPagedItems,
} from "../../utils/common";
import {
  Table as CSDTable,
  Box,
  Header,
  SpaceBetween,
  CollectionPreferencesProps,
} from "@cloudscape-design/components-themed";
import { Link } from "react-router-dom";
import { COMMON_LABELS } from "../default-constants";
import { LABELS, CONTRACT_ROUTE, CLASSIFICATION } from "./constants";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import type { RootState } from "../../app/store";
import { useGetExaminationStatusQuery } from "../../features/api";
import { ExaminationStatusSearchResponse } from "../../features/api-interfaces";
import {
  setColumnWidths,
  setPageIndex,
  changePageSize,
} from "../../features/slices/examinations-status-list";

// 表示用レコードの型定義
interface ExaminationStatusSearchViewItem
  extends ExaminationStatusSearchResponse {
  examinationRequestUnitLabel: string;
}

// カラム定義
const columnItemBase = [
  {
    id: "examinationApplicationNo",
    header: LABELS.EXAMINATION_APPLICATION_NO,
    cell: (e: ExaminationStatusSearchViewItem) => (
      // 審査内容照会画面遷移
      <Link to={"/examination-inquiry/" + e.examinationApplicationNo}>
        {e.examinationApplicationNo}
      </Link>
    ),
  },
  {
    id: "applicationNo",
    header: LABELS.APPLICATION_NO,
    cell: (e: ExaminationStatusSearchViewItem) => e.applicationNo,
  },
  {
    id: "serviceName",
    header: LABELS.SERVICE_NAME,
    cell: (e: ExaminationStatusSearchViewItem) => e.contractServiceName,
  },
  {
    id: "applicationClassification",
    header: LABELS.APPLICATION_CLASSIFICATION,
    cell: (e: ExaminationStatusSearchViewItem) => e.applicationType,
  },
  {
    id: "examinationType",
    header: LABELS.EXAMINATION_TYPE,
    cell: (e: ExaminationStatusSearchViewItem) => e.examinationType,
  },
  {
    id: "contractRoute",
    header: LABELS.CONTRACT_ROUTE,
    cell: (e: ExaminationStatusSearchViewItem) =>
      e.contractRouteName ? e.contractRouteName : CONTRACT_ROUTE.DIRECT,
  },
  {
    id: "companyName",
    header: LABELS.COMPANY_NAME,
    cell: (e: ExaminationStatusSearchViewItem) => e.companyName,
  },
  {
    id: "merchantName",
    header: LABELS.MERCHANT_NAME,
    cell: (e: ExaminationStatusSearchViewItem) => e.merchantName,
  },
  {
    id: "examinationRequestUnit",
    header: LABELS.EXAMINATION_REQUEST_UNIT,
    cell: (e: ExaminationStatusSearchViewItem) => e.examinationRequestUnitLabel,
  },
  {
    id: "examinationStatus",
    header: LABELS.EXAMINATION_STATUS,
    cell: (e: ExaminationStatusSearchViewItem) => e.examinationStatus,
  },
];

interface ITable {
  isBackFromDetail: boolean;
  skip: boolean;
}

/**
 * 審査状況・POS登録状況照会 一覧エリア
 */
const Table = (props: ITable) => {
  const { isBackFromDetail, skip } = props;
  const dispatch = useAppDispatch();
  const { searchCriteria, tableConfig } = useAppSelector(
    (state: RootState) => state.examinationsStatusList
  );
  const { pageSize, pageIndex, columnWidths } = tableConfig;
  // //一覧データの取得
  const { data, isFetching } = useGetExaminationStatusQuery(searchCriteria, {
    skip,
  });

  /**
   * 通常審査の場合ShopIDを付与する
   * @param {string} applicationType
   * @param {string} examinationRequestUnit
   * @param {number} shopId
   * @returns {string} examinationRequestUnitLabel
   */
  const getExaminationRequestUnitLabel = (
    applicationType: string,
    examinationRequestUnit: string,
    shopId: number
  ) => {
    let examinationRequestUnitLabel = examinationRequestUnit;
    if (applicationType === CLASSIFICATION.NORMAL_APPLICATION) {
      if (shopId !== null && shopId !== undefined) {
        examinationRequestUnitLabel =
          examinationRequestUnitLabel +
          COMMON_LABELS.LEFT_PARENTHESIS +
          shopId +
          COMMON_LABELS.RIGHT_PARENTHESIS;
      }
    }
    return examinationRequestUnitLabel;
  };

  // 一覧データの加工
  const listData = data
    ? data.map((item: ExaminationStatusSearchResponse) => ({
        ...item,
        examinationRequestUnitLabel: getExaminationRequestUnitLabel(
          item.applicationType,
          item.examinationRequestUnit,
          item.shopid
        ),
      }))
    : [];

  const columnDefinitions = columnItemBase.map((column, index) => {
    const width = isBackFromDetail ? columnWidths[index] : undefined;
    return { ...column, width };
  });
  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));

  const [pagedItems, setPagedItems] = useState<Object[]>([]);
  const [allItems, setAllItems] = useState<Object[]>([]);
  useEffect(() => {
    if (listData !== undefined && !isFetching) {
      setPagedItems(getPagedItems(listData, pageIndex, pageSize));
      setAllItems(listData);
      setCurrentPageIndex(
        adjustPageIndex(listData.length, pageSize, pageIndex)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, pageIndex, pageSize]);

  /**
   * テーブルのカラム幅を変更したときの処理.
   * @param {number[]} widths カラム幅リスト
   */
  const onColumnWidthsChange = (widths: number[]) =>
    dispatch(setColumnWidths(widths));

  /**
   * ページ番号を変更したときの処理.
   * @param {number} currentPageIndex 現在のページ番号
   */
  const setCurrentPageIndex = (currentPageIndex: number) =>
    dispatch(setPageIndex(currentPageIndex));

  /**
   * ページサイズを変更したときの処理.
   * @param {CollectionPreferencesProps.Preferences} detail
   */
  const handleOnConfirm = (detail: CollectionPreferencesProps.Preferences) =>
    dispatch(changePageSize(detail.pageSize!));

  return (
    <CSDTable
      columnDefinitions={columnDefinitions}
      loading={isFetching}
      loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
      items={pagedItems}
      onColumnWidthsChange={({ detail: { widths } }) =>
        onColumnWidthsChange(widths as number[])
      }
      resizableColumns
      stickyHeader
      header={
        <SpaceBetween size={"xs"}>
          <Header>
            {LABELS.TITLE}
            {getHeaderCounterText(allItems.length, pagedItems.length)}
          </Header>
        </SpaceBetween>
      }
      pagination={
        <Pagination
          currentPageIndex={pageIndex}
          filteredItems={allItems}
          setCurrentPageIndex={setCurrentPageIndex}
          pageSize={pageSize}
          setPagedItems={setPagedItems}
        />
      }
      preferences={
        <Preference
          preferences={{ pageSize, visibleContent: [] }}
          options={options}
          handleOnConfirm={handleOnConfirm}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          {COMMON_LABELS.CSD.NO_DATA}
        </Box>
      }
    />
  );
};

export default Table;
